import { faMars, faVenus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useEffect, useState, useCallback } from "react"
import {
  TAassignUserToApplication,
  TAapplicationIsCalled,
  TAapplicationInfoState,
  TAsetShareDate,
  TAsetDelayReason,
  TAsetIsWpMessaged,
  TAsetPricing,
  TAsetStatePayload,
  TAfindApplicationByCampaignIdPaginated,
  TAdowngradeApplicationState,
  TAcanceledStateChange,
  TAalterApplicationState,
} from "../services/applicationAPI"
import ApplicationProfileDetailModal from "./ApplicationProfileDetail"
import { UpdateApplicationModal } from "./UpdateApplicationModal"
import { UpdateAddressModal } from "./UpdateAddressModal"
import { numFixer } from "../pages/User/getAllUsers/components/Functions"
import { CheckBadgeIcon } from "@heroicons/react/20/solid"
import EditUserPriceBrand from "../pages/Campaign/findCampaign/components/editPriceUserBrand"
import AssingUser from "../pages/Campaign/findCampaign/components/assignUser"
import EditApplicationNote from "../pages/Campaign/findCampaign/components/editApplicationNote"
import { selectUser } from "../redux/store/auth/authSlice"
import { useSelector } from "react-redux"
import VoiceMessagesList from "./VoiceMessagesList"
import EditShareDate from "./EditShareDate"
import EditStatePayload from "./EditStatePayload"
import { ApplicationDoc, ExtendedApplicationDoc } from "../types/applicationData"
import { useNavigate } from "react-router-dom"
import ConfirmModal from "./ConfirmModal"
import { generateAvailableStates } from "../static/Application"
import { applicationStateLabels } from "../pages/Campaign/findCampaign/FindCampaign"

interface DetailedApplicationsTableProps {
  selectedState: string
  campaignId: string
  barter: boolean | undefined
}

const delayReasonOptions = [
  { value: "", label: "— Seçiniz —" },
  { value: "health_issue", label: "Sağlık sorunu" },
  { value: "personal_issue", label: "Kişisel sebep" },
  { value: "technical_issue", label: "Teknik sorun" },
  { value: "logistics_issue", label: "Lojistik" },
  { value: "other", label: "Diğer" },
]

function formatShareDate(val: string | Date | null | undefined) {
  if (!val) {
    return "—"
  }

  let d = typeof val === "string" ? new Date(val) : val

  if (Number.isNaN(d.getTime()) && typeof val === "string") {
    const [y, m, day] = val.split("-").map(Number)
    d = new Date(y, (m ?? 1) - 1, day ?? 1)
  }

  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

const DetailedApplicationsTable: React.FC<DetailedApplicationsTableProps> = ({
  selectedState,
  campaignId,
  barter,
}) => {
  const navigate = useNavigate()
  const [application_data, setApplication_data] = useState<ExtendedApplicationDoc[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalItems, setTotalItems] = useState<number>(0)
  const [perPage, setPerPage] = useState(10)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newPricingBrand, setNewPricingBrand] = useState<number>(0)
  const [newPricingUser, setNewPricingUser] = useState<number>(0)
  const [application, setApplication] = useState<ExtendedApplicationDoc | null>(null)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [selectedApp, setSelectedApp] = useState<ExtendedApplicationDoc | null>(null)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [selectedAppForAddress, setSelectedAppForAddress] = useState<ExtendedApplicationDoc | null>(
    null,
  )
  const [assignUserModal, setAssignUserModal] = useState(false)
  const [user_id, setUserId] = useState("")
  const [selectedUser, setSelectedUser] = useState<ExtendedApplicationDoc | null>(null)
  const [isOpenDetailModal, setIsOpenDetailModal] = useState<boolean>(false)
  const [stateModalOpen, setStateModalOpen] = useState(false)
  const [isShareDateModal, setIsShareDateModal] = useState<boolean>(false)
  const [shareDate, setShareDate] = useState<Date | null>(null)
  const [infoState, setInfoState] = useState("")
  const [isStatePayloadModal, setIsStatePayloadModal] = useState<boolean>(false)
  const [postUrl, setPostUrl] = useState("")
  const [productSent, setProductSent] = useState<number>(0)
  const [pendingDelay, setPendingDelay] = useState<Record<string, { code: string; note: string }>>(
    {},
  )
  const admin = useSelector(selectUser)
  const clearAdmin = admin.replace(/['"]+/g, "")
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<ExtendedApplicationDoc | null>(
    null,
  )
  const [applicationStateChange, setApplicationStateChange] = useState<
    ((application: ExtendedApplicationDoc) => Promise<void>) | null
  >(null)
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<Set<string>>(new Set())
  const [bulkNewState, setBulkNewState] = useState<string>("")
  const [bulkAvailableStates, setBulkAvailableStates] = useState<string[]>([])
  const [isBulkUpdating, setIsBulkUpdating] = useState<boolean>(false)

  const fetchApplications = useCallback(
    async (page = 1) => {
      try {
        setLoading(true)
        setError(null)
        const response = await TAfindApplicationByCampaignIdPaginated(
          campaignId,
          selectedState,
          page,
          perPage,
        )
        if (response) {
          if (selectedState === "saved_users") {
            setApplication_data(response.savedUsers || [])
            setTotalItems(response.savedUsers?.length || 0)
            setTotalPages(Math.ceil((response.savedUsers?.length || 0) / perPage))
          } else {
            setApplication_data(response.applications || [])
            setTotalItems(response.applicationsCount || 0)
            setTotalPages(Math.ceil((response.applicationsCount || 0) / perPage))
          }
          setCurrentPage(page)
        }
      } catch (error: unknown) {
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          (error as { status: number }).status === 401
        ) {
          navigate("/login")
        }
        setError(error instanceof Error ? error.message : "An error occurred")
      } finally {
        setLoading(false)
      }
    },
    [campaignId, selectedState, perPage, navigate],
  )

  const reload = () => {
    fetchApplications(currentPage)
  }

  useEffect(() => {
    fetchApplications(1)
  }, [selectedState, campaignId, fetchApplications])

  useEffect(() => {
    handleProductSent(application_data)
  }, [application_data])

  // Clear selections when state tab changes
  useEffect(() => {
    setSelectedApplicationIds(new Set())
    setBulkNewState("")
  }, [selectedState])

  useEffect(() => {
    if (selectedState && application_data.length > 0) {
      const firstApp = application_data[0]
      if (firstApp) {
        generateAvailableStates(selectedState, barter || false, setBulkAvailableStates)
      }
    }
  }, [selectedState, barter, application_data])

  function getPending(app: ExtendedApplicationDoc) {
    return {
      code: pendingDelay[app._id]?.code ?? app.delay_reason_code ?? "",
      note: pendingDelay[app._id]?.note ?? app.delay_reason_note ?? "",
    }
  }

  const handleProductSent = (application_data: ExtendedApplicationDoc[]) => {
    const productSent =
      application_data.filter((app: ExtendedApplicationDoc) =>
        app.state_history?.find((state: { state: string }) => state.state === "product_sent"),
      ).length || 0
    setProductSent(productSent)
  }

  const handleOpenPricingModal = (application: ExtendedApplicationDoc) => {
    setApplication(application)
    setNewPricingBrand(application.price_brand || application.user?.price_brand || 0)
    setNewPricingUser(application.price_user || application.user?.price_user || 0)
    setIsModalOpen(true)
  }

  const handleOpenShareDateModal = (application: ExtendedApplicationDoc) => {
    setApplication(application)
    setIsShareDateModal(true)
  }

  const handleOpenUpdateModal = (app: ExtendedApplicationDoc) => {
    setSelectedApp(app)
    setIsUpdateModalOpen(true)
  }

  const handleCloseUpdateModal = () => {
    setSelectedApp(null)
    setIsUpdateModalOpen(false)
  }

  const handleOpenAddressModal = (app: ExtendedApplicationDoc) => {
    setSelectedAppForAddress(app)
    setIsAddressModalOpen(true)
  }

  const handleCloseAddressModal = () => {
    setSelectedAppForAddress(null)
    setIsAddressModalOpen(false)
  }

  const handleClosePricingModal = () => {
    setIsModalOpen(false)
    setApplication(null)
    setNewPricingUser(0)
    setNewPricingBrand(0)
  }

  const handleCloseShareDateModal = () => {
    setSelectedApp(null)
    setIsShareDateModal(false)
  }

  const handleOpenInfoModal = (application: ExtendedApplicationDoc) => {
    setApplication(application)
    setInfoState(application.info_state || "")
    setStateModalOpen(true)
  }

  const handleCloseInfoModal = () => {
    setIsModalOpen(false)
    setApplication(null)
  }

  const handleOpenStatePayloadModal = (application: ExtendedApplicationDoc) => {
    setApplication(application)
    setIsStatePayloadModal(true)
  }

  const handleCloseStatePayloadModal = () => {
    setIsStatePayloadModal(false)
    setApplication(null)
    setPostUrl("")
  }

  const handleWhatsappMessaged = async (application_id: string, value: boolean) => {
    try {
      await TAsetIsWpMessaged(application_id, value)
      reload()
    } catch {
      alert("WhatsApp işaretleme güncellenemedi")
    }
  }

  const handleSaveDelayReason = async (app: ExtendedApplicationDoc) => {
    const { code, note } = getPending(app)
    if (!code) {
      return alert("Lütfen bir gecikme nedeni seçin")
    }
    try {
      await TAsetDelayReason(app._id, code, note)
      setPendingDelay((p) => ({ ...p, [app._id]: { code, note } }))
      reload()
    } catch {
      alert("Gecikme nedeni kaydedilemedi")
    }
  }

  const handleSavePricingData = async (app_id: string) => {
    try {
      const response = await TAsetPricing(newPricingUser, newPricingBrand, app_id)
      if (response) {
        setIsModalOpen(false)
        setApplication(null)
        reload()
      }
    } catch (error) {
      alert("Fiyat güncellenirken hata oluştu")
    }
  }

  const handleShareDate = async (app: ExtendedApplicationDoc) => {
    try {
      if (!shareDate) {
        return alert("Lütfen bir tarih seçin")
      }
      await TAsetShareDate(app._id, shareDate)
      handleCloseShareDateModal()
    } catch (error) {
      alert("Error updating application")
    }
  }

  const handleAssignUser = async (application_id: string, user_id: string) => {
    try {
      const response = await TAassignUserToApplication(application_id, user_id)
      if (response.status === 200) {
        alert("User assigned to application successfully")
        reload()
      }
    } catch (error) {
      alert("Error assigning user to application")
    }
  }

  const handleIsCalled = async (
    application_id: string,
    is_called: boolean,
    is_called_by: string,
  ) => {
    try {
      const response = await TAapplicationIsCalled(application_id, is_called, is_called_by)
      if (response) {
        setIsModalOpen(false)
      }
    } catch (error) {
      alert("Error updating application")
    }
  }

  const handleInfoState = async (application_id: string, info_state: string) => {
    try {
      const response = await TAapplicationInfoState(application_id, info_state)
      if (response) {
        setStateModalOpen(false)
      }
    } catch (error) {
      alert("Error updating application")
    }
  }

  const handleSaveStatePayload = async (application: ExtendedApplicationDoc, postUrl: string) => {
    try {
      const response = await TAsetStatePayload(application._id, postUrl)
      if (response) {
        handleCloseStatePayloadModal()
        reload()
      }
    } catch (error) {
      alert("Durum içeriği güncellenirken hata oluştu")
    }
  }

  const handleDowngradeApplication = async (application: ExtendedApplicationDoc) => {
    try {
      const response = await TAdowngradeApplicationState(application._id)
      if (response) {
        reload()
      }
      setShowConfirmModal(false)
      setSelectedApp(null)
    } catch (error) {
      alert("Uygulama durumu düşürülürken hata oluştu")
    }
  }

  const handleCanceledStateChange = async (application: ExtendedApplicationDoc) => {
    try {
      const response = await TAcanceledStateChange(application._id)
      if (response) {
        reload()
      }
      setShowConfirmModal(false)
      setSelectedApplication(null)
    } catch (error) {
      alert("Uygulama durumu düşürülürken hata oluştu")
    }
  }

  const handleToggleSelectApplication = (applicationId: string) => {
    setSelectedApplicationIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId)
      } else {
        newSet.add(applicationId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedApplicationIds.size === application_data.length) {
      setSelectedApplicationIds(new Set())
    } else {
      setSelectedApplicationIds(new Set(application_data.map((app) => app._id)))
    }
  }

  const handleBulkStateChange = async () => {
    if (!bulkNewState) {
      alert("Lütfen bir durum seçin")
      return
    }
    if (selectedApplicationIds.size === 0) {
      alert("Lütfen en az bir başvuru seçin")
      return
    }

    setIsBulkUpdating(true)
    try {
      const selectedApplications = application_data.filter((app) =>
        selectedApplicationIds.has(app._id),
      )

      const updatePromises = selectedApplications.map(async (app) => {
        try {
          const applicationData: Partial<ApplicationDoc> & { _id: string } = {
            _id: app._id,
            ...(typeof app.user === "string" && { user: app.user }),
            instauser: app.instauser || "",
            campaign: app.campaign || "",
            state: app.state,
            price_user: app.price_user || 0,
            price_brand: app.price_brand || 0,
            state_history: app.state_history || [],
            last_action_at: new Date(),
            application_date: app.application_date || new Date(),
            insta_username: app.insta_username || "",
            tiktok_username: app.tiktok_username || "",
            language: app.language || "",
            insta_keywords: app.insta_keywords || [],
            address: app.address || {
              contactName: "",
              contactPhone: "",
              city: "",
              country: "",
              address: "",
              details: "",
              zipCode: "",
              id: "",
              extra_information: "",
            },
            verification: app.verification || false,
            is_banned: app.is_banned || false,
            prioritized: app.prioritized || false,
            short_url: app.short_url || "",
          }
          const payload = {}
          await TAalterApplicationState(applicationData, bulkNewState, payload)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Failed to update application ${app._id}:`, error)
          throw error
        }
      })

      await Promise.all(updatePromises)
      setSelectedApplicationIds(new Set())
      setBulkNewState("")
      reload()
      alert(`${selectedApplications.length} başvurunun durumu başarıyla güncellendi`)
    } catch (error) {
      alert("Bazı başvuruların durumu güncellenirken hata oluştu")
    } finally {
      setIsBulkUpdating(false)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      fetchApplications(currentPage - 1)
    }
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      fetchApplications(currentPage + 1)
    }
  }

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchApplications(page)
    }
  }

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage)
  }

  const getPageNumbers = (): number[] => {
    const pages: number[] = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    return pages
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => fetchApplications(currentPage)}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Tekrar Dene
        </button>
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-4">
      {selectedState === "waiting_content" && barter && (
        <div className="rounded-lg border border-gray-200 bg-gradient-to-r from-indigo-50 via-white to-pink-50 px-4 py-3 text-center text-sm font-medium text-gray-700 shadow-sm">
          {productSent} Ürün Teslim Edildi / {application_data.length - productSent} Ürün Teslim Edilmedi
        </div>
      )}

      {/* Bulk Action Toolbar */}
      {application_data.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={
                  application_data.length > 0 &&
                  selectedApplicationIds.size === application_data.length
                }
                onChange={handleSelectAll}
                className="form-checkbox h-5 w-5 text-indigo-600"
              />
              <div className="text-sm text-gray-700">
                <span className="font-semibold">{selectedApplicationIds.size}</span> seçili başvuru
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Durum Değiştir:
              </label>
              <select
                value={bulkNewState}
                onChange={(e) => setBulkNewState(e.target.value)}
                className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={selectedApplicationIds.size === 0 || isBulkUpdating}
              >
                <option value="">Durum Seçin</option>
                {bulkAvailableStates.map((state) => {
                  const stateLabel =
                    applicationStateLabels[state as keyof typeof applicationStateLabels] ?? state
                  return (
                    <option key={state} value={state}>
                      {stateLabel}
                    </option>
                  )
                })}
              </select>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkStateChange}
                  disabled={
                    selectedApplicationIds.size === 0 ||
                    !bulkNewState ||
                    isBulkUpdating
                  }
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {isBulkUpdating ? "Güncelleniyor..." : "Uygula"}
                </button>
                {selectedApplicationIds.size > 0 && (
                  <button
                    onClick={() => {
                      setSelectedApplicationIds(new Set())
                      setBulkNewState("")
                    }}
                    className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                  >
                    Seçimi Temizle
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm max-h-[800px]">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 w-12 bg-gray-50">
              <input
                type="checkbox"
                checked={
                  application_data.length > 0 &&
                  selectedApplicationIds.size === application_data.length
                }
                onChange={handleSelectAll}
                className="form-checkbox h-4 w-4 text-indigo-600"
              />
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Kullanıcı
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Kaynak
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Ban Durumu
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Durumu Güncelle
            </th>
            {(selectedState === "brand_canceled" ||
              selectedState === "user_canceled" ||
              selectedState === "account_rejected") && (
              <>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
                  Başvuran Profiller Durumuna Geçir
                </th>
              </>
            )}
            {selectedState === "content_approved" && (
              <>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
                  Paylaşılacak İçerikler Durumuna Geçir
                </th>
              </>
            )}
            {selectedState === "application_done" && (
              <>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
                  Durum İçeriği
                </th>
              </>
            )}
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Takipçi Sayısı
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Şehir
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Yaş
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Cinsiyet
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Etkileşim Oranı
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Kullanıcı/Marka Tutarı
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Paylaşım Tarihi
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              WP Mesaj Atıldı
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
              Gecikme Nedeni
            </th>

            {selectedState === "waiting_content" && (
              <>
                {barter && (
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
                    Ürün Teslim Durumu
                  </th>
                )}
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
                  Aranma Durumu
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
                  Ekstra Not
                </th>
              </>
            )}

            {["waiting_content", "invite_new", "waiting_address"].includes(selectedState) && (
              <>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50">
                  Sesli Mesajlar
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {application_data &&
            application_data.map((app: ExtendedApplicationDoc, index: number) => (
              <tr
                key={app._id || index}
                className="border-t border-gray-100 transition-colors hover:bg-gray-50"
              >
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={selectedApplicationIds.has(app._id)}
                    onChange={() => handleToggleSelectApplication(app._id)}
                    className="form-checkbox h-4 w-4 text-indigo-600"
                  />
                </td>
                {app.insta_username === undefined ? (
                  <td
                    className="px-3 py-3 text-gray-700 align-middle"
                    onClick={() => {
                      setSelectedUser(app)
                      setIsOpenDetailModal(true)
                    }}
                  >
                    <div className="flex flex-col items-center cursor-pointer whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <img
                          src={app.tiktok_profile_pic || app.user?.tiktok?.profile_pic}
                          alt={app._id}
                          className="w-12 h-12 rounded-xl"
                        />
                        {app.verification && <CheckBadgeIcon className="w-6 h-6 text-blue-500" />}
                      </div>
                      <div>@{app.tiktok_username || app.user?.tiktok?.username}</div>
                    </div>
                  </td>
                ) : (
                  <td
                    className="px-3 py-3 text-gray-700 align-middle"
                    onClick={() => {
                      setSelectedUser(app)
                      setIsOpenDetailModal(true)
                    }}
                  >
                    <div className="flex flex-col items-center cursor-pointer whitespace-nowrap">
                      <div className="relative w-12 h-12">
                        <img
                          src={app.insta_profile_pic || app.user?.insta?.profile_pic}
                          alt={app._id}
                          className="w-full h-full rounded-xl object-cover"
                        />
                        {app.verification && (
                          <CheckBadgeIcon className="absolute top-1/2 -right-6 transform -translate-y-1/2 w-6 h-6 text-blue-500" />
                        )}
                      </div>
                      <div className="mt-1 text-sm">
                        @{app.insta_username || app.user?.insta?.username}
                      </div>
                    </div>
                  </td>
                )}
                <td className="px-3 py-3 text-gray-700 align-middle">
                  <div className="flex items-center justify-center w-full">
                    {app.created_from_admin ? (
                      <div className="flex items-center justify-center w-full">Admin</div>
                    ) : (
                      <div className="flex items-center justify-center w-full">Kullanıcı</div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-700 align-middle">
                  <div className="flex items-center justify-center w-full">
                    {app.is_banned ? (
                      <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
                        Banlı
                      </span>
                    ) : (
                      <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                        Banlı Değil
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-700 align-middle">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 text-xs"
                      onClick={() => handleOpenUpdateModal(app)}
                    >
                      Düzenle
                    </button>
                    <button
                      className="text-green-600 bg-green-100 rounded px-2 py-1 text-xs"
                      onClick={() => handleOpenAddressModal(app)}
                    >
                      Adres
                    </button>
                  </div>
                </td>
                {(selectedState === "brand_canceled" ||
                  selectedState === "user_canceled" ||
                  selectedState === "account_rejected") && (
                  <>
                    <td className="px-3 py-3 text-gray-700 align-middle">
                      <div className="flex items-center justify-center">
                        <button
                          className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 text-xs"
                          onClick={() => {
                            setApplicationStateChange(() => handleCanceledStateChange)
                            setSelectedApplication(app)
                            setShowConfirmModal(true)
                          }}
                        >
                          Geçir
                        </button>
                      </div>
                    </td>
                  </>
                )}
                {selectedState === "content_approved" && (
                  <>
                    <td className="px-3 py-3 text-gray-700 align-middle">
                      <div className="flex items-center justify-center">
                        <button
                          className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 text-xs"
                          onClick={() => {
                            setApplicationStateChange(() => handleDowngradeApplication)
                            setSelectedApplication(app)
                            setShowConfirmModal(true)
                          }}
                        >
                          Geçir
                        </button>
                      </div>
                    </td>
                  </>
                )}
                {selectedState === "application_done" && (
                  <>
                    <td className="px-3 py-3 text-gray-700 align-middle">
                      <div className="flex flex-col items-center justify-center w-40 whitespace-nowrap">
                        <div className="text-center">
                          {(() => {
                            try {
                              if (typeof app.state_payload === "string" && app.state_payload) {
                                const parsed = JSON.parse(app.state_payload)
                                return (
                                  <div className="space-y-1">
                                    {parsed.view && (
                                      <div className="text-sm">
                                        <span className="font-medium">İzlenme:</span>{" "}
                                        {parsed.view.toLocaleString()}
                                      </div>
                                    )}
                                    {parsed.like && (
                                      <div className="text-sm">
                                        <span className="font-medium">Beğeni:</span>{" "}
                                        {parsed.like.toLocaleString()}
                                      </div>
                                    )}
                                    {parsed.comment && (
                                      <div className="text-sm">
                                        <span className="font-medium">Yorum:</span>{" "}
                                        {parsed.comment.toLocaleString()}
                                      </div>
                                    )}
                                    {parsed.share && (
                                      <div className="text-sm">
                                        <span className="font-medium">Paylaşım:</span>{" "}
                                        {parsed.share}
                                      </div>
                                    )}
                                  </div>
                                )
                              }
                              return <span className="text-gray-500">Veri yok</span>
                            } catch (error) {
                              return <span className="text-gray-500">Geçersiz veri</span>
                            }
                          })()}
                        </div>
                        <button
                          className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 text-xs mt-2"
                          onClick={() => handleOpenStatePayloadModal(app)}
                        >
                          Düzenle
                        </button>
                      </div>
                    </td>
                  </>
                )}
                {app.insta_followers === undefined ? (
                  <td className="px-3 py-3 text-gray-700 align-middle">
                    {app.tiktok_followers || app.user?.tiktok?.followers}
                  </td>
                ) : (
                  <td className="px-3 py-3 text-gray-700 align-middle">
                    {app.insta_followers || app.user?.insta?.followers}
                  </td>
                )}
                <td className="px-3 py-3 text-gray-700 align-middle">
                  {(app && app.city && app?.city?.charAt(0).toUpperCase() + app?.city?.slice(1)) ||
                    (app &&
                      app.user?.city &&
                      app?.user?.city?.charAt(0).toUpperCase() + app?.user?.city?.slice(1))}
                </td>
                <td className="px-3 py-3 text-gray-700 align-middle">
                  {app && app.age ? (app.age > 65 ? "65" : app.age) : app.user?.age}
                </td>
                <td className="px-3 py-3 text-gray-700 align-middle">
                  <div className="text-center items-center">
                    {app.gender === "male" ? (
                      <FontAwesomeIcon icon={faMars} style={{ color: "#005eff" }} />
                    ) : app.gender === "female" ? (
                      <FontAwesomeIcon icon={faVenus} style={{ color: "#ff00dd" }} />
                    ) : (
                      <div>diğer</div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-700 align-middle">{`${numFixer(
                  app.insta_engagement_rate || app.user?.insta?.engagement_rate || 0,
                )}%`}</td>
                <td className="px-3 py-3 text-gray-700 align-middle">
                  <div className="flex flex-col items-center justify-center w-32 whitespace-nowrap">
                    <p>
                      {app.price_user || app.user?.price_user} /{" "}
                      {app.price_brand || app.user?.price_brand}{" "}
                      {app.currency || app.user?.currency}
                    </p>
                    <button
                      className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 ml-6 mt-1 text-xs"
                      onClick={() => handleOpenPricingModal(app)}
                    >
                      Düzenle
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-700 align-middle">
                  <div className="flex flex-col items-center justify-center w-32 whitespace-nowrap">
                    <p>{formatShareDate(app.share_date)}</p>
                    <button
                      className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 ml-6 mt-1 text-xs"
                      onClick={() => handleOpenShareDateModal(app)}
                    >
                      Düzenle
                    </button>
                  </div>
                </td>
                {/* WP Mesaj Atıldı */}
                <td className="px-3 py-3 text-gray-700 align-middle">
                  <div className="flex flex-col items-center justify-center w-32 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-indigo-600"
                      checked={!!app.is_whatsapp_messaged}
                      onChange={() => handleWhatsappMessaged(app._id, !app.is_whatsapp_messaged)}
                    />
                    {app.whatsapp_messaged_at && (
                      <span className="text-xs mt-1">
                        {formatShareDate(app.whatsapp_messaged_at)}
                      </span>
                    )}
                  </div>
                </td>

                {/* Gecikme Nedeni */}
                <td className="px-3 py-3 text-gray-700 align-middle">
                  {(() => {
                    const pending = getPending(app)
                    return (
                      <div className="flex flex-col items-center justify-center w-40">
                        <select
                          className="border rounded px-2 py-1 text-sm w-full"
                          value={pending.code}
                          onChange={(e) =>
                            setPendingDelay((p) => ({
                              ...p,
                              [app._id]: { code: e.target.value, note: pending.note },
                            }))
                          }
                        >
                          {delayReasonOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <input
                          className="border rounded px-2 py-1 text-sm w-full mt-1"
                          placeholder="Not (opsiyonel)"
                          value={pending.note}
                          onChange={(e) =>
                            setPendingDelay((p) => ({
                              ...p,
                              [app._id]: { code: pending.code, note: e.target.value },
                            }))
                          }
                        />

                        <button
                          className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 mt-1 text-xs"
                          onClick={() => handleSaveDelayReason(app)}
                        >
                          Kaydet
                        </button>
                      </div>
                    )
                  })()}
                </td>

                {selectedState === "waiting_content" && (
                  <>
                    {barter && (
                      <td className="px-3 py-3 text-gray-700 align-middle">
                        <div>
                          <p>
                            {app.state_history?.find(
                              (state: { state: string }) => state.state === "product_sent",
                            )
                              ? "Teslim Edildi"
                              : "Teslim Edilmedi"}
                          </p>
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-3 text-gray-700 align-middle">
                      <div className="flex flex-col items-center justify-center w-32 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600"
                          checked={app.is_called}
                          onChange={() => {
                            handleIsCalled(app._id, !app.is_called, clearAdmin)
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-700 align-middle">
                      <div>
                        <p>{app.info_state}</p>
                        <button
                          className="text-indigo-600 bg-indigo-100 rounded px-2 py-1 ml-6 mt-1 text-xs"
                          onClick={() => handleOpenInfoModal(app)}
                        >
                          Düzenle
                        </button>
                      </div>
                    </td>
                  </>
                )}
                {["waiting_content", "invite_new", "waiting_address"].includes(selectedState) && (
                  <>
                    <td className="px-3 py-3 text-gray-700 align-middle">
                      <div className="flex items-center justify-between">
                        <VoiceMessagesList record={app} />
                      </div>
                    </td>
                  </>
                )}
                <AssingUser
                  modalOpen={assignUserModal}
                  onClose={() => setAssignUserModal(false)}
                  app={app}
                  handleAssignUser={handleAssignUser}
                  user_id={user_id}
                  setUserId={setUserId}
                  setAssignUserModal={setAssignUserModal}
                />
              </tr>
            ))}
        </tbody>
      </table>
      </div>

      {/* Enhanced Pagination */}
      <div className="flex flex-col sm:flex-row justify-start items-center mt-6 space-y-4 sm:space-y-0 sm:space-x-8">
        {/* Items per page selector */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Sayfa başına:</span>
          <select
            value={perPage}
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            {/* <option value={500}>500</option> */}
            {/* <option value={1000}>1000</option> */}
          </select>
        </div>

        {/* Page info */}
        <div className="text-sm text-gray-700">
          {totalItems > 0 && (
            <span>
              {(currentPage - 1) * perPage + 1}-{Math.min(currentPage * perPage, totalItems)} /{" "}
              {totalItems} kayıt
            </span>
          )}
        </div>

        {/* Pagination controls */}
        {application_data.length > 0 && (
          <div className="flex items-center space-x-1">
            {/* Always show basic navigation */}
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              Önceki
            </button>

            <span className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded">
              {currentPage} / {totalPages > 1 ? totalPages : 1}
            </span>

            <button
              onClick={goToNextPage}
              disabled={currentPage >= totalPages}
              className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              Sonraki
            </button>

            {/* Advanced pagination - only show if we have multiple pages */}
            {totalPages > 1 && (
              <>
                {/* First page */}
                {currentPage > 3 && (
                  <>
                    <button
                      onClick={() => goToPage(1)}
                      className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      1
                    </button>
                    {currentPage > 4 && <span className="text-gray-500">...</span>}
                  </>
                )}

                {/* Page numbers */}
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-2 py-1 text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      page === currentPage
                        ? "bg-blue-500 text-white"
                        : "bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {/* Last page */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && <span className="text-gray-500">...</span>}
                    <button
                      onClick={() => goToPage(totalPages)}
                      className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ApplicationProfileDetailModal
        data={selectedUser}
        isOpen={isOpenDetailModal}
        onClose={() => {
          setIsOpenDetailModal(false)
        }}
      />

      <EditUserPriceBrand
        modalOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        newPricingUser={newPricingUser}
        setNewPricingUser={setNewPricingUser}
        newPricingBrand={newPricingBrand}
        setNewPricingBrand={setNewPricingBrand}
        handleSavePricingData={() => application?._id && handleSavePricingData(application._id)}
        handleClosePricingModal={handleClosePricingModal}
      />
      <EditApplicationNote
        modalOpen={stateModalOpen}
        onClose={() => setStateModalOpen(false)}
        infoState={infoState}
        setInfoState={setInfoState}
        handleSaveState={handleInfoState}
        handleCloseInfoModal={handleCloseInfoModal}
        application={application}
      />
      {selectedApp && (
        <UpdateApplicationModal
          isOpen={isUpdateModalOpen}
          onClose={handleCloseUpdateModal}
          _id={selectedApp._id}
          campaign_id={selectedApp.campaign}
          state={selectedApp.state}
          application={selectedApp as ApplicationDoc}
        />
      )}
      <EditShareDate
        modalOpen={isShareDateModal}
        onClose={() => setIsShareDateModal(false)}
        shareDate={shareDate}
        setShareDate={setShareDate}
        handleShareDate={handleShareDate}
        handleCloseModal={handleCloseShareDateModal}
        application={application}
      />

      {application && (
        <EditStatePayload
          modalOpen={isStatePayloadModal}
          onClose={() => setIsStatePayloadModal(false)}
          postUrl={postUrl}
          setPostUrl={setPostUrl}
          handleSaveStatePayload={(application: ExtendedApplicationDoc) =>
            handleSaveStatePayload(application, postUrl)
          }
          handleCloseModal={handleCloseStatePayloadModal}
          application={application}
        />
      )}

      {selectedAppForAddress && selectedAppForAddress._id && (
        <UpdateAddressModal
          isOpen={isAddressModalOpen}
          onClose={handleCloseAddressModal}
          applicationId={selectedAppForAddress._id}
          currentAddress={selectedAppForAddress.address}
        />
      )}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        selectedApplication={selectedApplication}
        applicationStateChange={applicationStateChange}
      />
    </div>
  )
}

export default DetailedApplicationsTable
